// src/services/fishIQWaterSpecies.ts
import type { FishHabitatType, FishIQSpeciesSeed, Hotspot } from '../types';
import { FISHIQ_SPECIES_SEED } from '../constants/fishIQSeed';
import {
  fishIQSpeciesAllowedForAuthoritativeRegion,
  getFishIQRegionalCandidateKeys,
  getFishIQRegionalSpeciesSignal,
  normalizeFishIQSpeciesKey,
} from '../constants/fishIQRegionalProfiles';
import { getFishIQRegionHintsForContext, getFishIQRegionIdsForContext } from './fishIQRegionDetector';
import { fishIQSpeciesAllowedForWaterContext, resolveWaterContext } from './fishIQWaterContext';
import { applyFishIQCatchabilityFacts } from '../data/fishIQCatchabilityFacts';
import { canonicalSpeciesKey, getCanonicalFishIQMetadata, dedupeFishIQSpeciesForPresentation } from '../data/fishIQCanonicalSpecies';

export type NormalizedWaterHabitat =
  | 'lake'
  | 'river'
  | 'reservoir'
  | 'pond'
  | 'creek'
  | 'inshore'
  | 'estuary'
  | 'bay'
  | 'ocean'
  | 'canal'
  | 'marsh'
  | 'surf'
  | 'reef'
  | 'backwater'
  | 'other';

const normalize = (s: any) => String(s ?? '').trim().toLowerCase();

const SALT_HINTS = [
  'ocean', 'sea', 'gulf', 'pacific', 'atlantic', 'harbor', 'harbour', 'bay', 'sound', 'inlet',
  'beach', 'marina', 'estuary', 'delta', 'pier', 'surf', 'coast', 'brackish', 'strait', 'port', 'mangrove', 'nearshore'
];

const SALTWATER_HABITATS: NormalizedWaterHabitat[] = ['ocean', 'inshore', 'bay', 'estuary', 'marsh', 'surf', 'reef', 'backwater'];
const FRESHWATER_FALLBACK_HABITATS: NormalizedWaterHabitat[] = ['lake', 'reservoir', 'pond', 'river', 'creek', 'canal'];

const isSaltwaterHabitat = (value: any) => SALTWATER_HABITATS.includes(normalize(value) as NormalizedWaterHabitat);
const isFreshwaterHabitat = (value: any) => FRESHWATER_FALLBACK_HABITATS.includes(normalize(value) as NormalizedWaterHabitat);

type HotspotWaterContextInput = {
  waterKind?: string | null;
  waterName?: string | null;
  waterType?: string | null;
  lat?: number | null;
  lon?: number | null;
  regionHint?: string | null;
};

const freshwaterFallbackFor = (rawWater: NormalizedWaterHabitat): NormalizedWaterHabitat => {
  if (rawWater === 'marsh' || rawWater === 'estuary' || rawWater === 'bay' || rawWater === 'inshore' || rawWater === 'surf' || rawWater === 'ocean' || rawWater === 'reef' || rawWater === 'backwater') return 'lake';
  return rawWater === 'other' ? 'lake' : rawWater;
};

export function resolveFishIQHotspotWaterContext(input: HotspotWaterContextInput): {
  rawWater: NormalizedWaterHabitat;
  water: NormalizedWaterHabitat;
  contextClass: ReturnType<typeof resolveWaterContext>['contextClass'];
  coastalAllowed: boolean;
} {
  const rawWater = normalizeHotspotWaterType(input);
  const resolvedContext = resolveWaterContext({
    lat: input.lat,
    lon: input.lon,
    waterName: input.waterName,
    waterKind: input.waterKind,
    waterType: input.waterType || rawWater,
    regionHint: input.regionHint,
  });
  const contextClass = resolvedContext.contextClass;

  const explicitFreshwater =
    resolvedContext.freshwaterSignal ||
    contextClass === 'freshwater' ||
    contextClass === 'inland' ||
    isFreshwaterHabitat(rawWater);

  const coastalAllowed =
    !explicitFreshwater &&
    (
      contextClass === 'coastal' ||
      contextClass === 'offshore' ||
      resolvedContext.nearCoastSignal ||
      isSaltwaterHabitat(rawWater)
    );

  // If the map only knows a broad coastal-state region, do not let a salt habitat
  // leak inland. Explicit freshwater/inland terms already win in the detector.
  // But for island/coastal Asia pings with no waterName yet, allow the coordinate
  // coastline heuristic to lift unknown/generic water into an inshore read.
  const water =
    isSaltwaterHabitat(rawWater) && !coastalAllowed
      ? freshwaterFallbackFor(rawWater)
      : rawWater === 'other' && coastalAllowed
        ? 'inshore'
        : rawWater;

  return { rawWater, water, contextClass, coastalAllowed };
}

const toTitle = (s: string) => s.replace(/[_-]+/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());

function hashString(input: string) {
  let h = 2166136261;
  for (let i = 0; i < input.length; i += 1) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return Math.abs(h >>> 0);
}

function stableShuffle<T>(arr: T[], seedKey: string): T[] {
  const out = [...arr];
  for (let i = out.length - 1; i > 0; i -= 1) {
    const h = hashString(`${seedKey}:${i}`);
    const j = h % (i + 1);
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

export function normalizeHotspotWaterType(input: {
  waterKind?: string | null;
  waterName?: string | null;
  waterType?: string | null;
}): NormalizedWaterHabitat {
  const kind = normalize(input?.waterKind);
  const type = normalize(input?.waterType);
  const name = normalize(input?.waterName);
  const hay = `${kind} ${type} ${name}`;

  // Freshwater/inland terms win before broad coastal words like "bay".
  // This protects names such as "Lake Laguna de Bay", reservoirs, canals,
  // dams, basins, wetlands, and river systems inside coastal countries.
  if (/(creek|stream|brook)/.test(hay)) return 'creek';
  if (/(canal|channel|ditch|drain)/.test(hay)) return 'canal';
  if (/(reservoir|dam|basin)/.test(hay)) return 'reservoir';
  if (/(great lakes|lake|freshwater profile|fresh water profile|inland water|wetland)/.test(hay)) return 'lake';
  if (/pond/.test(hay)) return 'pond';
  if (/river/.test(hay)) return 'river';

  if (/(backwater|backwaters)/.test(hay)) return 'backwater';
  if (/(reef|coral|bommie)/.test(hay)) return 'reef';
  if (/(surf|beach|shorebreak|wash)/.test(hay)) return 'surf';
  if (/(ocean|sea|gulf|pacific|atlantic|strait)/.test(hay)) return 'ocean';
  if (/(coast|coastal|pier|jetty|breakwater|port|nearshore|mangrove)/.test(hay)) return 'inshore';
  if (/(estuary|delta|lagoon)/.test(hay)) return 'estuary';
  if (/(bay|harbor|harbour|sound|marina)/.test(hay)) return 'bay';
  if (/marsh/.test(hay)) return 'marsh';

  if (kind === 'waterway') return 'river';
  if (kind === 'reservoir') return 'reservoir';
  if (kind === 'freshwater' || kind === 'fresh water' || kind === 'inland') return 'lake';

  if (SALT_HINTS.some((x) => name.includes(x))) return name.includes('ocean') ? 'ocean' : 'inshore';
  if (kind === 'water') return 'lake';
  return 'other';
}

function compatibleHabitats(h: NormalizedWaterHabitat): NormalizedWaterHabitat[] {
  switch (h) {
    case 'reef': return ['reef', 'inshore', 'ocean', 'bay'];
    case 'backwater': return ['backwater', 'estuary', 'bay', 'inshore', 'marsh', 'canal'];
    case 'surf': return ['surf', 'ocean', 'inshore', 'bay', 'reef'];
    case 'ocean': return ['ocean', 'surf', 'inshore', 'bay', 'reef'];
    case 'bay': return ['bay', 'estuary', 'inshore', 'ocean'];
    case 'estuary': return ['estuary', 'bay', 'inshore', 'marsh', 'backwater'];
    case 'inshore': return ['inshore', 'bay', 'estuary', 'marsh', 'ocean', 'reef', 'backwater'];
    case 'marsh': return ['marsh', 'estuary', 'bay', 'inshore'];
    case 'river': return ['river', 'creek', 'canal', 'reservoir', 'lake'];
    case 'creek': return ['creek', 'river', 'pond', 'lake'];
    case 'canal': return ['canal', 'river', 'lake', 'reservoir'];
    case 'reservoir': return ['reservoir', 'lake', 'river', 'pond'];
    case 'pond': return ['pond', 'lake', 'reservoir', 'creek'];
    case 'lake': return ['lake', 'reservoir', 'pond', 'river'];
    // Unknown/generic water should stay freshwater-safe. Saltwater only enters when explicitly detected.
    default: return ['lake', 'river', 'reservoir', 'pond', 'creek', 'canal'];
  }
}

const WATER_SPECIES_PRIORITY: Record<NormalizedWaterHabitat, string[]> = {
  surf: [
    'barred_surfperch', 'surfperch', 'california_corbina', 'yellowfin_croaker', 'spotfin_croaker',
    'halibut', 'striped_bass', 'bluefish', 'pompano',
  ],
  bay: [
    'spotted_bay_bass', 'halibut', 'barred_sand_bass', 'kelp_bass', 'california_corbina',
    'yellowfin_croaker', 'spotfin_croaker', 'redfish', 'snook', 'speckled_trout',
    'flounder', 'black_drum', 'sheepshead', 'mangrove_snapper',
  ],
  estuary: [
    'redfish', 'snook', 'speckled_trout', 'flounder', 'black_drum', 'sheepshead',
    'striped_bass', 'white_seabass', 'spotted_bay_bass', 'halibut',
  ],
  inshore: [
    'kelp_bass', 'barred_sand_bass', 'spotted_bay_bass', 'halibut', 'white_seabass',
    'pacific_bonito', 'pacific_mackerel', 'yellowtail', 'rockfish', 'redfish',
    'snook', 'tarpon', 'spanish_mackerel',
  ],
  ocean: [
    'pacific_bonito', 'pacific_mackerel', 'yellowtail', 'white_seabass', 'rockfish',
    'kelp_bass', 'halibut', 'striped_bass', 'bluefish', 'spanish_mackerel',
  ],
  reef: ['grouper_sea', 'snapper_sea', 'coral_trout', 'giant_trevally_sea', 'bluefin_trevally', 'rabbitfish_sea', 'barracuda_sea', 'emperor_fish', 'jobfish'],
  backwater: ['barramundi', 'barramundi_india', 'pearlspot_india', 'mangrove_jack_sea', 'mangrove_jack_india', 'milkfish', 'fourfinger_threadfin_india', 'tarpon_indo_pacific'],
  marsh: ['redfish', 'speckled_trout', 'black_drum', 'flounder', 'snook', 'tarpon', 'largemouth_bass', 'channel_catfish'],
  lake: ['largemouth_bass', 'smallmouth_bass', 'spotted_bass', 'bluegill', 'black_crappie', 'white_crappie', 'walleye', 'yellow_perch', 'channel_catfish', 'common_carp', 'rainbow_trout'],
  reservoir: ['largemouth_bass', 'spotted_bass', 'smallmouth_bass', 'striped_bass', 'white_bass', 'walleye', 'black_crappie', 'blue_catfish', 'channel_catfish', 'common_carp'],
  pond: ['largemouth_bass', 'bluegill', 'redear_sunfish', 'black_crappie', 'channel_catfish', 'common_carp', 'rainbow_trout'],
  river: ['smallmouth_bass', 'largemouth_bass', 'rainbow_trout', 'brown_trout', 'brook_trout', 'steelhead', 'chinook_salmon', 'coho_salmon', 'channel_catfish', 'flathead_catfish', 'walleye', 'common_carp'],
  creek: ['brook_trout', 'rainbow_trout', 'brown_trout', 'smallmouth_bass', 'bluegill', 'largemouth_bass'],
  canal: ['largemouth_bass', 'peacock_bass', 'common_carp', 'channel_catfish', 'bluegill', 'snakehead', 'snook', 'tarpon'],
  other: [],
};

function speciesKeySet(s: FishIQSpeciesSeed): Set<string> {
  return new Set<string>(
    [s.id, s.slug, s.commonName, s.scientificName, ...(Array.isArray((s as any).aliases) ? (s as any).aliases : [])]
      .map(normalizeFishIQSpeciesKey)
      .filter((value): value is string => Boolean(value)),
  );
}

function waterSpecificBoost(s: FishIQSpeciesSeed, water: NormalizedWaterHabitat): number {
  const priorities = WATER_SPECIES_PRIORITY[water] || [];
  if (!priorities.length) return 0;
  const keys = speciesKeySet(s);
  const index = priorities.findIndex((key) => keys.has(normalizeFishIQSpeciesKey(key)));
  if (index < 0) return 0;
  if (index <= 3) return 18;
  if (index <= 8) return 13;
  return 9;
}


function regionalWaterPriorityBoost(
  s: FishIQSpeciesSeed,
  water: NormalizedWaterHabitat,
  input?: { lat?: number | null; lon?: number | null; regionHint?: string | null; waterName?: string | null },
): number {
  const ids = new Set(getFishIQRegionIdsForContext({
    lat: input?.lat,
    lon: input?.lon,
    waterName: input?.waterName,
    waterType: water,
    waterKind: water,
    regionHint: input?.regionHint,
  }));
  const keys = speciesKeySet(s);
  const has = (...values: string[]) => values.some((value) => keys.has(normalizeFishIQSpeciesKey(value)));

  if (ids.has('philippines')) {
    if (['lake', 'reservoir', 'pond', 'canal', 'marsh'].includes(water) && has('mudfish_dalag', 'striped_snakehead', 'walking_catfish', 'nile_tilapia', 'common_carp')) return 16;
    if (['river', 'creek', 'canal'].includes(water) && has('mudfish_dalag', 'striped_snakehead', 'walking_catfish', 'nile_tilapia')) return 13;
    if (['estuary', 'backwater', 'bay', 'marsh'].includes(water) && has('milkfish', 'barramundi', 'apahap_barramundi', 'mangrove_jack_sea', 'tarpon_indo_pacific', 'rabbitfish_sea')) return 18;
    if (water === 'reef' && has('grouper_sea', 'snapper_sea', 'coral_trout', 'rabbitfish_sea', 'giant_trevally_sea', 'bluefin_trevally', 'golden_trevally')) return 20;
    if (water === 'ocean' && has('yellowfin_tuna', 'yellowfin_tuna_sea', 'skipjack_tuna', 'bigeye_tuna', 'dogtooth_tuna', 'wahoo_sea', 'mahi_mahi', 'cobia_sea', 'black_marlin', 'blue_marlin_sea')) return 20;
  }

  if (ids.has('india_inland')) {
    if (['lake', 'reservoir', 'pond'].includes(water) && has('rohu_india', 'catla_india', 'mrigal_india', 'common_carp', 'tilapia_india')) return 18;
    if (['pond', 'canal', 'marsh'].includes(water) && has('striped_snakehead_india', 'great_snakehead_india', 'walking_catfish_india', 'climbing_perch_india')) return 18;
    if (['river', 'creek'].includes(water) && has('golden_mahseer_india', 'deccan_mahseer_india', 'wallago_catfish_india')) return 20;
  }

  if (ids.has('india_coastal')) {
    if (['estuary', 'backwater', 'bay', 'marsh'].includes(water) && has('pearlspot_india', 'barramundi_india', 'mangrove_jack_india', 'fourfinger_threadfin_india', 'hilsa_india')) return 20;
    if (water === 'reef' && has('giant_trevally_india', 'grouper_sea', 'snapper_sea', 'cobia_sea', 'seer_fish_india')) return 18;
    if (['inshore', 'surf', 'ocean'].includes(water) && has('indian_mackerel', 'seer_fish_india', 'queenfish_india', 'giant_trevally_india', 'fourfinger_threadfin_india')) return 17;
  }

  return 0;
}

function categoryFits(
  s: FishIQSpeciesSeed,
  water: NormalizedWaterHabitat,
  input?: { lat?: number | null; lon?: number | null; regionHint?: string | null; waterName?: string | null },
) {
  return fishIQSpeciesAllowedForWaterContext(s, {
    lat: input?.lat,
    lon: input?.lon,
    waterName: input?.waterName,
    waterKind: water,
    waterType: water,
    regionHint: input?.regionHint,
  });
}

function scoreSpecies(s: FishIQSpeciesSeed, water: NormalizedWaterHabitat, waterName?: string | null, input?: { lat?: number | null; lon?: number | null; regionHint?: string | null }) {
  const habitats = (s.habitats || []).map((x) => normalize(x)) as FishHabitatType[];

  // Hard stops run before regional boosts/fallback diversity. Water-context guards
  // protect inland reads; the regional guard prevents known Atlantic/Gulf species
  // from leaking into Pacific reads (and vice versa) when a specific U.S. coastal
  // basin is known.
  if (!categoryFits(s, water, { ...input, waterName })) return -1000;
  if (!fishIQSpeciesAllowedForAuthoritativeRegion(s, {
    lat: input?.lat,
    lon: input?.lon,
    waterName,
    waterType: water,
    waterKind: water,
    regionHint: input?.regionHint,
  })) return -1000;
  if (!isSaltwaterHabitat(water) && s.category === 'saltwater') return -1000;

  let score = 0;
  if (habitats.includes(water as any)) score += 24;
  for (const c of compatibleHabitats(water)) if (habitats.includes(c as any)) score += c === water ? 0 : 10;
  const regionalSignal = getFishIQRegionalSpeciesSignal(s, {
    lat: input?.lat,
    lon: input?.lon,
    waterName,
    waterType: water,
    waterKind: water,
    regionHint: input?.regionHint,
  });
  if (regionalSignal) score += regionalSignal.scoreBoost;
  score += waterSpecificBoost(s, water);
  score += regionalWaterPriorityBoost(s, water, { ...input, waterName });
  const name = normalize(waterName);
  if (name && /channel|canal/.test(name) && habitats.includes('canal' as any)) score += 7;
  if (name && /harbor|harbour|marina|bay/.test(name) && (habitats.includes('bay' as any) || habitats.includes('inshore' as any))) score += 7;
  if (name && /river|creek|stream/.test(name) && (habitats.includes('river' as any) || habitats.includes('creek' as any))) score += 7;
  return score;
}

export function pickSpeciesForHotspotWater(input: {
  waterKind?: string | null;
  waterName?: string | null;
  waterType?: string | null;
  hotspotId?: string | null;
  count?: number;
  lat?: number | null;
  lon?: number | null;
  regionHint?: string | null;
}): FishIQSpeciesSeed[] {
  const { water } = resolveFishIQHotspotWaterContext(input);
  const regionalKeys = new Set(getFishIQRegionalCandidateKeys({
    lat: input.lat,
    lon: input.lon,
    waterName: input.waterName,
    waterKind: input.waterKind,
    waterType: water,
    regionHint: input.regionHint,
  }));

  const scored = (FISHIQ_SPECIES_SEED as FishIQSpeciesSeed[])
    .map((raw) => applyFishIQCatchabilityFacts(raw) as FishIQSpeciesSeed)
    .map((s) => {
      const speciesKeys = [s.id, s.slug, s.commonName, ...(Array.isArray((s as any).aliases) ? (s as any).aliases : [])].map(normalizeFishIQSpeciesKey);
      const regionalNudge = speciesKeys.some((key) => regionalKeys.has(key)) ? 6 : 0;
      return { s, score: scoreSpecies(s, water, input.waterName, input) + regionalNudge };
    })
    .filter((x) => x.score > -900);

  const count = Math.max(1, Math.min(20, Number(input.count ?? 8))); // keep area reads populated; salt/fresh guards still run above
  const tieSeed = `${input.hotspotId || 'fish'}:${water}:${input.waterName || ''}:${input.lat || ''}:${input.lon || ''}`;
  const tieOrder = new Map(
    stableShuffle(scored.map((x) => x.s), tieSeed).map((item, index) => [normalize(item?.id || item?.slug || item?.commonName || ''), index]),
  );

  // Rank by water-fit first. Stable shuffle only breaks ties so the list can
  // feel alive without burying the strongest local matches.
  return scored
    .sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      const ak = normalize(a.s?.id || a.s?.slug || a.s?.commonName || '');
      const bk = normalize(b.s?.id || b.s?.slug || b.s?.commonName || '');
      return (tieOrder.get(ak) ?? 0) - (tieOrder.get(bk) ?? 0);
    })
    .slice(0, count)
    .map((x) => x.s);
}

function mapTime(value?: string): string {
  const v = normalize(value);
  if (v.includes('night')) return 'Evening';
  if (v.includes('dusk') || v.includes('sunset') || v.includes('evening')) return 'Evening';
  if (v.includes('dawn') || v.includes('sunrise') || v.includes('morning')) return 'Morning';
  return 'Day';
}

function buildSpeciesDetailFromSeed(
  picked: FishIQSpeciesSeed,
  hotspot?: { waterKind?: string | null; waterName?: string | null; waterType?: string | null; lat?: number | null; lon?: number | null; regionHint?: string | null },
) {
  const normalizedWater = resolveFishIQHotspotWaterContext({
    waterKind: hotspot?.waterKind,
    waterName: hotspot?.waterName,
    waterType: (hotspot as any)?.waterType,
    lat: (hotspot as any)?.lat,
    lon: (hotspot as any)?.lon,
    regionHint: (hotspot as any)?.regionHint || (hotspot as any)?.region,
  }).water;
  const waterLabel = toTitle(normalizedWater);
  const waterName = String(hotspot?.waterName || '').trim();
  const waterKind = String(hotspot?.waterKind || normalizedWater || '').trim();

  const canonicalSpecies = getCanonicalFishIQMetadata(picked);

  return {
    id: picked.id,
    canonicalSpeciesId: canonicalSpecies.canonicalId,
    canonicalSpecies,
    speciesId: picked.id,
    slug: picked.slug,
    commonName: picked.commonName,
    name: picked.commonName,
    scientificName: picked.scientificName || '',
    summary: picked.summary,
    description: picked.summary,
    habitats: picked.habitats || [],
    regions: picked.regions || [],
    beginnerTips: picked.beginnerTips || [],
    advancedTips: picked.advancedTips || [],
    aquaticKind: (picked as any).aquaticKind || 'finfish',
    waterRealms: (picked as any).waterRealms || [],
    harvestModes: (picked as any).harvestModes || [],
    edibleCommonly: Boolean((picked as any).edibleCommonly),
    fishiqEligible: (picked as any).fishiqEligible !== false,
    caution: (picked as any).caution || '',
    feedingProfile: picked.feedingProfile || { primaryPrey: [] },
    tactics: {
      bestBaits: picked.bestBaits || [],
      bestTechniques: picked.bestTechniques || [],
      bestSeasons: picked.bestSeasons || [],
      bestTimesOfDay: picked.bestTimesOfDay || [],
    },
    bestBaits: picked.bestBaits || [],
    bestTechniques: picked.bestTechniques || [],
    bestSeasons: picked.bestSeasons || [],
    bestTimesOfDay: picked.bestTimesOfDay || [],
    waterName,
    waterKind,
    waterType: normalizedWater,
    waterMatchLabel: waterName
      ? `${picked.commonName} is included in the ${waterName} area profile.`
      : `${picked.commonName} is part of the ${waterLabel} species profile.`,
    notes: `${picked.commonName} is part of the ${waterLabel} species profile.`,
    fishiqActivityScore: scoreSpecies(picked, normalizedWater, waterName),
    regulatoryFlags: [
      ...(['crustacean', 'mollusk', 'cephalopod', 'echinoderm'].includes(String((picked as any).aquaticKind || '').toLowerCase()) ? ['check_rules'] : []),
      ...(String((picked as any).caution || '').trim() ? ['caution'] : []),
    ],
  } as any;
}

export function getSpeciesDetailForHotspot(hotspot: any) {
  return hotspot?.fishIQSpeciesData || null;
}

type DecorateHotspotOptions = {
  speciesSeed?: FishIQSpeciesSeed | null;
  fallbackRank?: number;
  areaSpeciesCount?: number;
};

const uniqueSpeciesSeeds = (items: FishIQSpeciesSeed[]) => {
  return dedupeFishIQSpeciesForPresentation(items, { suppressLocalGroups: true });
};

export function getSpeciesCandidatesForHotspot(hotspot: Hotspot, count = 12): FishIQSpeciesSeed[] {
  return uniqueSpeciesSeeds(
    pickSpeciesForHotspotWater({
      waterKind: (hotspot as any)?.waterKind,
      waterName: (hotspot as any)?.waterName,
      waterType: (hotspot as any)?.waterType,
      hotspotId: hotspot.id,
      count,
      lat: Number((hotspot as any)?.position?.lat ?? (hotspot as any)?.lat),
      lon: Number((hotspot as any)?.position?.lng ?? (hotspot as any)?.position?.lon ?? (hotspot as any)?.lng ?? (hotspot as any)?.lon),
      regionHint: (hotspot as any)?.regionHint || (hotspot as any)?.region || null,
    }),
  );
}

export function decorateHotspotWithSpecies(
  hotspot: Hotspot,
  index = 0,
  options?: DecorateHotspotOptions,
): Hotspot {
  const candidates = getSpeciesCandidatesForHotspot(hotspot, Math.max(8, Number(options?.areaSpeciesCount ?? 12)));
  const picked = options?.speciesSeed || candidates[Math.max(0, Number(options?.fallbackRank ?? 0))] || candidates[0];
  if (!picked) return hotspot;

  const technique = picked.bestTechniques?.[index % Math.max(1, picked.bestTechniques?.length || 1)] || 'Work likely bait and cover naturally.';
  const bait = picked.bestBaits?.[index % Math.max(1, picked.bestBaits?.length || 1)] || '';
  const time = mapTime(picked.bestTimesOfDay?.[index % Math.max(1, picked.bestTimesOfDay?.length || 1)]);
  const waterType = resolveFishIQHotspotWaterContext({
    waterKind: (hotspot as any)?.waterKind,
    waterName: (hotspot as any)?.waterName,
    waterType: (hotspot as any)?.waterType,
    lat: Number((hotspot as any)?.position?.lat ?? (hotspot as any)?.lat),
    lon: Number((hotspot as any)?.position?.lng ?? (hotspot as any)?.position?.lon ?? (hotspot as any)?.lng ?? (hotspot as any)?.lon),
    regionHint: (hotspot as any)?.regionHint || (hotspot as any)?.region || null,
  }).water;
  const areaContext = {
    waterKind: (hotspot as any)?.waterKind,
    waterName: (hotspot as any)?.waterName,
    waterType: (hotspot as any)?.waterType,
    lat: Number((hotspot as any)?.position?.lat ?? (hotspot as any)?.lat),
    lon: Number((hotspot as any)?.position?.lng ?? (hotspot as any)?.position?.lon ?? (hotspot as any)?.lng ?? (hotspot as any)?.lon),
    regionHint: (hotspot as any)?.regionHint || (hotspot as any)?.region || null,
  };

  const areaSpeciesData = candidates.map((candidate) =>
    buildSpeciesDetailFromSeed(candidate, areaContext),
  );

  return {
    ...hotspot,
    name: `${picked.commonName} (Area Ping)`,
    species: picked.commonName as any,
    timeOfDay: time as any,
    tactic: bait ? `${technique} Try ${bait} around ${hotspot.waterName || toTitle(waterType)}.` : technique,
    funFact: picked.summary || `${picked.commonName} is part of the ${toTitle(waterType)} species profile.`,
    fishIQSpeciesData: buildSpeciesDetailFromSeed(picked, areaContext),
    fishIQAreaSpeciesData: areaSpeciesData,
  } as any;
}

export function decorateHotspotsWithUniqueSpecies(hotspots: Hotspot[], areaSpeciesCount = 12): Hotspot[] {
  const used = new Set<string>();

  return (hotspots || []).map((hotspot, index) => {
    const candidates = getSpeciesCandidatesForHotspot(hotspot, Math.max(8, areaSpeciesCount));
    const picked =
      candidates.find((candidate) => {
        const key = canonicalSpeciesKey(candidate);
        if (!key || used.has(key)) return false;
        used.add(key);
        return true;
      }) || candidates[0] || null;

    const pickedKey = picked ? canonicalSpeciesKey(picked) : '';
    if (pickedKey) used.add(pickedKey);

    return decorateHotspotWithSpecies(hotspot, index, {
      speciesSeed: picked,
      areaSpeciesCount,
    });
  });
}
